import type { LogDescriptor } from '../core/log/client';
import type { LogRegistry } from '../core/loglist/registry';
import { shortLogName } from '../core/loglist/slug';
import { href, matchPath, parseRoutePath, type QueryInit, type Route } from '../router';
import { fmtInt } from './format';
import { registry } from './state/logs';

/** Log-page tabs, in the order they are shown. */
export type LogTab = 'overview' | 'entries' | 'inclusion' | 'consistency';
export const LOG_TAB_LABEL: Record<LogTab, string> = { overview: 'Overview', entries: 'Entries', inclusion: 'Inclusion', consistency: 'Consistency' };

/** Route path (no '#') of a log page: logRoute(log) → '/logs/google/argon2026h2', logRoute(log, 'entry/5'). */
export function logRoute(log: LogDescriptor, sub?: string, reg: LogRegistry = registry.value): string {
  const { op, log: slug } = reg.pathOf(log);
  return `/logs/${op}/${slug}${sub ? '/' + sub : ''}`;
}

/** Href of a log page (what goes into an anchor). */
export function logPath(log: LogDescriptor, sub?: string, query?: QueryInit): string {
  return href(logRoute(log, sub), query);
}

export function operatorPath(operator: string, reg: LogRegistry = registry.value): string {
  return href(`/logs/${reg.pathOf({ operator } as LogDescriptor).op}`);
}

export interface LogRouteInfo {
  log: LogDescriptor;
  tab: LogTab;
  index?: number;
}

/** Which log (and tab / entry) a route path addresses, if it is a log page. */
export function logFromRoute(path: string, reg: LogRegistry = registry.value): LogRouteInfo | undefined {
  const patterns: Array<[string, LogTab]> = [
    ['/logs/:op/:log', 'overview'],
    ['/logs/:op/:log/entries', 'entries'],
    ['/logs/:op/:log/entry/:index', 'entries'],
    ['/logs/:op/:log/inclusion', 'inclusion'],
    ['/logs/:op/:log/consistency', 'consistency'],
  ];
  for (const [pattern, tab] of patterns) {
    const m = matchPath(pattern, path);
    if (!m) continue;
    const log = reg.byPath(m.op!, m.log!);
    if (!log) return undefined;
    const index = m.index !== undefined ? Number(m.index) : undefined;
    return { log, tab, ...(index !== undefined && Number.isFinite(index) ? { index } : {}) };
  }
  return undefined;
}

/**
 * Human label for a route, used by return strips: "entry #1,000 of Argon2026h2", "Argon2026h2 › Entries",
 * "SCT Inspector". Undefined when the path is not one of ours.
 */
export function describeRoute(routeOrPath: string | Route, reg: LogRegistry = registry.value): string | undefined {
  const r = typeof routeOrPath === 'string' ? parseRoutePath(routeOrPath) : routeOrPath;
  const path = r.path;
  if (path === '/' || path === '/logs') return 'All logs';
  const op = matchPath('/logs/:op', path);
  if (op) return reg.operatorBySlug(op.op!) ?? op.op!;
  const info = logFromRoute(path, reg);
  if (info) {
    const name = shortLogName(info.log);
    if (info.index !== undefined) return `entry #${fmtInt(info.index)} of ${name}`;
    if (info.tab === 'overview') return name;
    return `${name} › ${info.tab === 'inclusion' ? 'Inclusion proof' : info.tab === 'consistency' ? 'Consistency proof' : LOG_TAB_LABEL[info.tab]}`;
  }
  return { '/submit': 'Submit', '/sct': 'SCT Inspector', '/settings': 'Settings', '/about': 'About', '/credits': 'About' }[path];
}

/** Hash href for a 'path?query' string (e.g. a `from` value). */
export function hrefOf(routePath: string): string {
  return '#' + routePath;
}
